import { create } from 'zustand'
import { User } from 'firebase/auth'
import { Upload, ProcessingProgress, Platform } from '@/types'
import { FirestoreService } from '@/services/firestoreService'
import { EarningsService } from '@/services/earningsService'
import { PlatformDetectionService } from '@/services/platformDetectionService'
import { DataExtractionService } from '@/services/dataExtractionService'
import { UploadProcessingService } from '@/services/uploadProcessingService'
import { getCurrentPeriod } from '@/utils/periodHelpers'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'

export type ProcessingStatus = 'uploading' | 'processing' | 'matching' | 'preparing' | null

interface UploadStore {
  uploads: Upload[]
  currentUpload: Upload | null
  isUploading: boolean
  uploadProgress: number
  error: string | null
  
  // Processing overlay state
  processingStatus: ProcessingStatus
  processingProgress: ProcessingProgress | null

  // Actions
  loadUploads: (user: User) => Promise<void>
  uploadFile: (file: File, period: string, platform: string, user: User) => Promise<string>
  deleteUpload: (uploadId: string, user: User) => Promise<void>
  setCurrentUpload: (upload: Upload | null) => void
  clearError: () => void
  
  // Processing actions
  setProcessingStatus: (status: ProcessingStatus, progress?: ProcessingProgress | null) => void

  // NEW: Staging-based processing
  processUploadStaging: (uploadId: string, user: User) => Promise<void>
  retryStagingProcessing: (uploadId: string, user: User) => Promise<void>
}

export const useUploadStore = create<UploadStore>((set, get) => ({
  uploads: [],
  currentUpload: null,
  isUploading: false,
  uploadProgress: 0,
  error: null,
  
  // Processing overlay state
  processingStatus: null,
  processingProgress: null,

  loadUploads: async (user: User) => {
    try {
      const firestoreService = new FirestoreService(user)
      const uploads = await firestoreService.getUploads()
      set({ uploads, error: null })
    } catch (error) {
      console.error('Failed to load uploads:', error)
      set({ error: 'Kunde inte ladda uppladdningar' })
    }
  },

  uploadFile: async (file: File, period: string, platform: string, user: User) => {
    // Start with uploading overlay
    // Prevent multiple concurrent uploads
    if (get().isUploading) {
      console.warn('[Upload] Upload already in progress, ignoring duplicate request')
      return
    }

    get().setProcessingStatus('uploading')
    set({ isUploading: true, uploadProgress: 0, error: null })

    try {
      // Verify user authentication token is valid
      try {
        await user.getIdToken(false)
      } catch (tokenError) {
        console.error('[Upload] Failed to get user token:', tokenError)
        throw new Error('User authentication invalid')
      }

      const firestoreService = new FirestoreService(user)
      
      // Parse file
      set({ uploadProgress: 25 })
      const arrayBuffer = await file.arrayBuffer()
      const workbook = XLSX.read(arrayBuffer, {
        type: 'array',
        codepage: 65001 // UTF-8
      })
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]]

      // Use header: 1 to get array of arrays, preserving exact column order
      const jsonData = XLSX.utils.sheet_to_json(firstSheet, {
        header: 1,
        raw: false,
        defval: ''
      }) as any[][]
      
      if (jsonData.length === 0) {
        throw new Error('Filen är tom')
      }
      
      // Extract headers (first row) and ensure they're strings
      const headers = (jsonData[0] as any[])
        .map(h => String(h || ''))
        .map(h => h.replace(/^\uFEFF/, '')) // Remove BOM character
      const dataRows = jsonData.slice(1).filter(row => 
        row && row.some(cell => cell !== '' && cell !== null && cell !== undefined)
      )
      
      if (dataRows.length === 0) {
        throw new Error('Inga datarader hittades i filen')
      }
      
      // Convert rows to objects preserving exact column order from CSV
      const data = dataRows.map(row => {
        const obj: Record<string, any> = {}
        // Ensure we create properties in the exact order they appear in headers
        headers.forEach((header, index) => {
          const cellValue = row[index]
          obj[header] = cellValue !== undefined && cellValue !== null ? String(cellValue) : ''
        })
        return obj
      })

      set({ uploadProgress: 50 })

      // PHASE 1: Platform Detection
      const platformInfo = PlatformDetectionService.detectPlatformFromFile(headers)

      set({ uploadProgress: 60 })

      // PHASE 2: Data Preparation for Staging (simplified)
      set({ uploadProgress: 75 })

      // Skip all staging creation during upload - this will be done in StagingWorkflowDialog
      set({ uploadProgress: 85 })

      // Clean up empty column headers and corresponding data
      const validHeaderIndices = headers.map((header, index) => header && header.trim() ? index : -1).filter(index => index !== -1)
      const cleanHeaders = validHeaderIndices.map(index => headers[index])
      const cleanData = data.map(row => {
        const cleanRow: any = {}
        validHeaderIndices.forEach(index => {
          const header = headers[index]
          if (header && header.trim()) {
            cleanRow[header] = row[header] || ''
          }
        })
        return cleanRow
      })

      // Create simplified upload record (no staging, no column mapping)
      const uploadData: Omit<Upload, 'id'> = {
        fileName: file.name,
        fileSize: file.size,
        rowCount: cleanData.length,
        headers: cleanHeaders,
        data: cleanData, // Store cleaned data for field mapping step
        platform: platformInfo.detectedPlatform || platform, // Use detected platform or fallback to parameter
        period: period || getCurrentPeriod(), // Use provided period or default to current period
        uploadedAt: new Date(),
        status: 'uploaded', // Set to uploaded immediately since no staging required
        processingMethod: 'pattern'
      }
      console.info('[Upload] Prepared upload payload:', {
        fileName: uploadData.fileName,
        platform: uploadData.platform,
        rowCount: uploadData.rowCount
      })

      let uploadId: string
      try {
        uploadId = await firestoreService.createUpload(uploadData)
        console.info('[Upload] Created upload document:', {
          uploadId,
          platform: uploadData.platform,
          rowCount: uploadData.rowCount
        })
      } catch (error) {
        console.error('[Upload] Failed to create upload document:', error)
        throw error
      }

      // Refresh uploads list
      await get().loadUploads(user)

      // Clear upload overlay and set final state in single update
      get().setProcessingStatus(null)

      set({
        uploadProgress: 0,
        isUploading: false
      })

      return uploadId
    } catch (error) {
      console.error('Failed to upload file:', error)
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      })

      // If upload was partially created, mark it as failed
      try {
        const firestoreService = new FirestoreService(user)
        // Try to find and mark the failed upload if it was created
        const uploads = await firestoreService.getUploads()
        const pendingUpload = uploads.find(u => u.fileName === file.name && u.status === 'pending')
        if (pendingUpload) {
          await firestoreService.updateUpload(pendingUpload.id, { status: 'failed' })
        }
      } catch (updateError) {
        console.error('Failed to update failed upload status:', updateError)
      }
      
      set({ 
        isUploading: false, 
        uploadProgress: 0,
        error: error instanceof Error ? error.message : 'Uppladdning misslyckades'
      })
    }
  },

  deleteUpload: async (uploadId: string, user: User) => {
    try {
      const firestoreService = new FirestoreService(user)

      // First, delete all related earnings (soft delete)
      const deletedEarningsCount = await EarningsService.deleteEarningsByUploadId(user, uploadId)

      // Then delete the upload document
      await firestoreService.deleteUpload(uploadId)

      // Remove from local state
      set(state => ({
        uploads: state.uploads.filter(upload => upload.id !== uploadId),
        currentUpload: state.currentUpload?.id === uploadId ? null : state.currentUpload,
        error: null
      }))

      // Show success toast with details
      if (deletedEarningsCount > 0) {
        toast.success('Uppladdning borttagen', {
          description: `Uppladdning och ${deletedEarningsCount} relaterade inkomstposter har tagits bort`
        })
      } else {
        toast.success('Uppladdning borttagen', {
          description: 'Uppladdningen har tagits bort (inga relaterade inkomstposter hittades)'
        })
      }

    } catch (error) {
      console.error('[UploadStore] Failed to delete upload:', error)
      const errorMessage = error instanceof Error ? error.message : 'Kunde inte ta bort uppladdning'
      set({ error: errorMessage })

      // Show error toast
      toast.error('Kunde inte ta bort uppladdning', {
        description: errorMessage
      })
    }
  },

  setCurrentUpload: (upload: Upload | null) => {
    set({ currentUpload: upload })
  },

  clearError: () => {
    set({ error: null })
  },

  // Processing actions
  setProcessingStatus: (status: ProcessingStatus, progress?: ProcessingProgress | null) => {
    set({
      processingStatus: status,
      processingProgress: progress || null
    })
  },

  // NEW: Staging-based processing
  processUploadStaging: async (uploadId: string, user: User) => {
    get().setProcessingStatus('processing')
    set({ error: null })

    try {
      const uploadProcessingService = new UploadProcessingService(user)

      // Get staging by upload ID, create if it doesn't exist
      let staging = await uploadProcessingService.getStagingByUploadId(uploadId)
      if (!staging) {
        // Get upload data to create staging
        const firestoreService = new FirestoreService(user)
        const upload = await firestoreService.getUpload(uploadId)
        if (!upload) {
          throw new Error('Upload not found')
        }

        // Create staging on-demand using the upload data
        const stagingData = DataExtractionService.createUploadStaging(
          uploadId,
          upload.platform as Platform,
          upload.period, // Pass period from upload
          [], // Start with empty normalized data - will be populated during field mapping
          { detectedPlatform: upload.platform, confidence: 1.0, allMatches: [], shouldAutoSelect: true }
        )

        await firestoreService.createUploadStaging(stagingData)

        // Get the newly created staging
        staging = await uploadProcessingService.getStagingByUploadId(uploadId)
        if (!staging) {
          throw new Error('Failed to create staging data')
        }
      }

      // Process the staging data
      const results = await uploadProcessingService.processUploadStaging(staging.id, (progress) => {
        set({ processingProgress: progress })
      })

      // Update upload status
      const firestoreService = new FirestoreService(user)
      await firestoreService.updateUpload(uploadId, { status: 'processed' })

      // Refresh uploads
      await get().loadUploads(user)

      get().setProcessingStatus(null)

      toast.success('Bearbetning slutförd', {
        description: `${results.matchedDrivers} förare matchade, ${results.newDriversNeeded} nya behövs`
      })

    } catch (error) {
      console.error('[UploadStore] Staging processing failed:', error)
      get().setProcessingStatus(null)

      const errorMessage = error instanceof Error ? error.message : 'Staging processing failed'
      set({ error: errorMessage })

      toast.error('Bearbetning misslyckades', {
        description: errorMessage
      })

      // Refresh uploads to show error status
      await get().loadUploads(user)
    }
  },

  retryStagingProcessing: async (uploadId: string, user: User) => {
    set({ error: null })

    try {
      const uploadProcessingService = new UploadProcessingService(user)

      // Get staging by upload ID
      const staging = await uploadProcessingService.getStagingByUploadId(uploadId)
      if (!staging) {
        throw new Error('No staging data found for this upload')
      }

      // Retry processing
      const results = await uploadProcessingService.retryProcessing(staging.id, (progress) => {
        set({ processingProgress: progress })
      })

      // Update upload status
      const firestoreService = new FirestoreService(user)
      await firestoreService.updateUpload(uploadId, { status: 'processed' })

      // Refresh uploads
      await get().loadUploads(user)

      toast.success('Ombearbetning slutförd', {
        description: `${results.matchedDrivers} förare matchade, ${results.newDriversNeeded} nya behövs`
      })

    } catch (error) {
      console.error('[UploadStore] Staging retry failed:', error)

      const errorMessage = error instanceof Error ? error.message : 'Retry failed'
      set({ error: errorMessage })

      toast.error('Ombearbetning misslyckades', {
        description: errorMessage
      })

      // Refresh uploads
      await get().loadUploads(user)
    }
  }
}))
